import { useLoaderData, type LoaderFunctionArgs } from 'react-router';
import type { FlowStep, FlowTrigger } from '@/types';
import { BackLink } from '@/components/back-link';
import { api } from '@/lib/api';
import { FlowBuilder } from './flow-builder';
import { FlowActions } from './flow-actions';
import { fmtDateTime, displayName } from '@/lib/format';
import { RowLink } from '@/components/row-link';

type Flow = { id: string; name: string; status: string; trigger: FlowTrigger; steps: FlowStep[]; reentry: string; stats: Record<string, number> };
type Run = { id: string; status: string; stepIndex: number; nextRunAt: string | null; createdAt: string; updatedAt: string; lastError: string | null; history: Array<{ stepIndex: number; type: string; result: string; at: string }>; contact: { id: string; email: string; firstName: string | null; lastName: string | null } | null };

export async function flowLoader({ params }: LoaderFunctionArgs) {
  const [flow, runs, templates, segments] = await Promise.all([
    api<Flow>(`/flows/${params.id}`),
    api<{ items: Run[]; total: number }>(`/flows/${params.id}/runs`),
    api<{ id: string; name: string; kind: string }[]>('/templates'),
    api<{ id: string; name: string; kind: string }[]>('/segments'),
  ]);
  return { flow, runs, templates, segments };
}

export function FlowPage() {
  const { flow, runs, templates, segments } = useLoaderData<typeof flowLoader>();
  return (
    <div className="space-y-5">
      <BackLink to="/flows">Flows</BackLink>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl">{flow.name}</h1>
          <p className="text-sm text-muted-foreground">
            {flow.status} · {flow.stats.total} runs ({flow.stats.running + flow.stats.waiting} active, {flow.stats.completed} completed, {flow.stats.exited} exited{flow.stats.failed ? `, ${flow.stats.failed} failed` : ''})
          </p>
        </div>
        <FlowActions flow={flow} />
      </div>
      <div className="grid gap-5 xl:grid-cols-[1fr_480px]">
        <FlowBuilder key={flow.id} templates={templates} segments={segments} initial={flow} />
        <div className="overflow-x-auto rounded-lg border bg-white">
          <div className="border-b px-3 py-2 text-sm text-muted-foreground">{runs.total} runs (latest 50)</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Contact</th>
                <th>Status</th>
                <th>Step</th>
                <th>Next</th>
                <th>Last</th>
              </tr>
            </thead>
            <tbody>
              {runs.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-muted-foreground">
                    No runs yet — activate the flow, then trigger events arrive from the sync.
                  </td>
                </tr>
              )}
              {runs.items.map((r) => (
                <RowLink key={r.id} href={r.contact ? `/contacts/${r.contact.id}` : '#'}>
                  <td>
                    {r.contact ? displayName(r.contact) : r.id}
                    <div className="text-xs text-muted-foreground">{r.contact?.email}</div>
                  </td>
                  <td>
                    {r.status}
                    {r.lastError && <div className="max-w-[180px] truncate text-xs text-red-700">{r.lastError}</div>}
                  </td>
                  <td>
                    {r.stepIndex}
                    <div className="text-xs text-muted-foreground">{r.history.slice(-1)[0]?.type ?? ''} {r.history.slice(-1)[0]?.result ?? ''}</div>
                  </td>
                  <td className="whitespace-nowrap text-xs">{fmtDateTime(r.nextRunAt)}</td>
                  <td className="whitespace-nowrap text-xs">{fmtDateTime(r.updatedAt)}</td>
                </RowLink>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
